import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CustomerdatabaseService } from './customerdatabase.service';
import moment from 'moment';

/**
 * This service is used to fetch all applicants from Firestore and store them in an array.
 * This service is also used to filter applicants based on certain conditions.
 * This service is also used to delete applicants from the array for display on kendo-grid.
 * This service is also used to return the array of applicants.
 * This service is also used to return the array of all, filtered, archived applicants.
 * 
 */
@Injectable({
  providedIn: 'root'
})
export class ApplicantService {

  public isApplicantDataFetchedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isApplicantDataFetched$: Observable<boolean> = this.isApplicantDataFetchedSubject.asObservable();
  public isApplicantDataUpdateNeededSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isApplicantDataUpdateNeeded$: Observable<boolean> = this.isApplicantDataUpdateNeededSubject.asObservable();
  public isFetchOldAndNewApplicantsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isFetchOldAndNewApplicants$: Observable<boolean> = this.isFetchOldAndNewApplicantsSubject.asObservable();
  allApplicants: any[] = [];
  filteredApplicants: any[] = [];
  archivedApplicants: any[] = [];
  allOldAndNewApplicants: any[] = [];
  contingentApplicants: any[];

  constructor(private customerDatabaseService: CustomerdatabaseService) { }

  /**
   * This method is used to fetch all applicants from Firestore by waiting for snapshot to resolve
   * and store them in an array.
   * @param snapshot 
   */

  getAllApplicants(snapshot: any) {
    this.allApplicants = snapshot;

    this.allApplicants.forEach((obj: any) => {
      if (obj.telefonnummer === undefined || obj.telefonnummer === null || obj.telefonnummer === "") {
        obj.telefonnummer = obj.mobilnummer || obj.phoneNumber;
      }
      if (obj.created._seconds != undefined) {
        obj.created.seconds = obj.created._seconds;
        obj.created.nanoseconds = obj.created._nanoseconds;
      }
    })
    // console.log("Fetching all applicants...");
    // console.log("Snapshot: ", snapshot);
    // let byteSizeTotal = 0;
    // snapshot.forEach((obj: any) => {
    //   var item = obj.data();
    //   let dataString = JSON.stringify(item);
    //   byteSizeTotal += new Blob([dataString]).size;
    //   item.id = obj.id;
    //   this.allApplicants.push(item);
    // });
    // console.log("Total byte size of all applicants: ", byteSizeTotal);
    this.isApplicantDataFetchedSubject.next(true);
    this.isApplicantDataUpdateNeededSubject.next(false);
  }

  // async getAllApplicants(snapshot: any): Promise<void> {
  //   const start = performance.now();
  //   this.allApplicants = [];
  //   console.log("Fetching all applicants...");
  //   try {
  //     // const snapshotApplicantObservable = await this.customerDB.getApplicantsWithCreated();      
  //     const subscriptionApplicant = snapshot.subscribe((snap) => {
  //       console.log("getAllApplicants:: Fetching all applicants... in snap: ", snap);
  //       snap.forEach((obj: any) => {
  //         var item = obj.data();
  //         item.id = obj.id;
  //         this.allApplicants.push(item);
  //       });
  //        this.isApplicantDataFetchedSubject.next(true);
  //        this.isApplicantDataUpdateNeededSubject.next(false);
  //     });
  //     const end = performance.now();
  //     console.log("getAllApplicants:: Time taken to fetch all applicants: ", (end - start) / 1000);     
  //     console.log(">>>>>>>>>>>>>> allApplicants docs: ", this.allApplicants);
  //     console.log(">>>>>>>>>>>>>> unsubcsribed: from subscriptionApplicant");
  //     subscriptionApplicant.unsubscribe();
  //   } catch (error) {
  //     console.error("Error fetching data from Firestore:", error);
  //     throw error;
  //   }
  // }

  /**
   * Function to filter applicants based on certain conditions.
   * @param applicantsToDelete 
   */
  deleteApplicantsForGrid(applicantsToDelete: any[]) {
    this.allApplicants = this.allApplicants.filter((applicant) => {
      return !applicantsToDelete.includes(applicant.id);
    });
    this.findAllFilteredApplicants();
    this.findAllArchivedApplicants();
  }

  /**
   * Add applicants to deleted applicants collection in Firestore.
   * @param applicantsToDelete 
   */
  async addToDeletedApplicants(applicantsToDelete) {
    for (let index = 0; index < applicantsToDelete.length; index++) {
      const element = applicantsToDelete[index];
      await this.customerDatabaseService.addToDeletedApplicants(element);
    }
  }

  /**
   * Add applicants to archived applicants collection in Firestore.
   * @param applicantsToArchive
   */

  addToArchivedApplicants(applicantsToArchive) {
    for (const archivedApplicant of applicantsToArchive) {
      this.customerDatabaseService.addToArchivedApplicants(archivedApplicant);
      for (const applicant of this.allApplicants) {
        if (applicant.id === archivedApplicant) {
          applicant.archived = true;
        }
      }
    }
  }

  /**
   * Function to filter applicants based on certain conditions 
   * like archived = false and where logs exist.
   */
  findAllFilteredApplicants() {
    this.filteredApplicants = [];
    this.filteredApplicants = this.allApplicants.filter((applicant) => {
      return !applicant.archived && applicant.logs && applicant.logs.length > 0;
    });
  }

  /**
   * Function to find all archived applicants
   */
  async findAllArchivedApplicants() {
    this.archivedApplicants = [];
    this.archivedApplicants = this.allApplicants.filter((applicant) => {
      if (applicant.archived) {
        return true;
      }
      return false;
    });
  }

  async reloadAllApplicantsLast30Days() {
    this.allApplicants = [];
    await this.customerDatabaseService.getAllApplicantsLast30Days().then((array) => {
      this.allApplicants = array;
      this.isApplicantDataUpdateNeededSubject.next(true);
    });
    return this.allApplicants;
  }

  /**
   * Function to set all applicants in the service.
   * @param data 
   */
  setAllApplicants(data, notifyUpdate: boolean = true) {
    this.allApplicants = data;
    if (notifyUpdate) {
      this.isApplicantDataUpdateNeededSubject.next(true);
    }
    this.findAllFilteredApplicants();
    this.findAllArchivedApplicants();
  }

  /**
   * @returns all applicants
   */
  returnAllApplicants() {
    return this.allApplicants;
  }

  /**
   * @returns filtered applicants
   */
  returnFilteredApplicants() {
    this.findAllFilteredApplicants();
    return this.filteredApplicants;
  }

  /**
   * @returns archived applicants
   */
  async returnArchivedApplicants() {
    await this.findAllArchivedApplicants();
    return this.archivedApplicants;
  }

  async reactivateApplicants(selectedApplicants) {
    for (const archivedApplicant of selectedApplicants) {
      this.customerDatabaseService.addToArchivedApplicants(archivedApplicant);
      for (const applicant of this.allApplicants) {
        if (applicant.id === archivedApplicant) {
          delete applicant.archived;
        }
      }
    }
  }

  /**
   * Function to fetch all new and old applicants from Firestore.
   */
  async fetchNewAndOldApplicants() {
    this.allOldAndNewApplicants = [];
    await this.customerDatabaseService.getAllNewAndOldApplicants().then((array) => {
      this.allOldAndNewApplicants = [...array];
      this.isFetchOldAndNewApplicantsSubject.next(true);
    });
    return this.allOldAndNewApplicants;
  }

  /**
   * @returns all old and new applicants
   */
  returnAllOldAndNewApplicants() {
    return this.allOldAndNewApplicants;
  }

  /**
   * Helper function to normalize applicant data (extracted from getAllApplicants logic)
   */
  private normalizeApplicant(item: any): void {
    if (item.telefonnummer === undefined || item.telefonnummer === null || item.telefonnummer === "") {
      item.telefonnummer = item.mobilnummer || item.phoneNumber;
    }
    if (item.created?._seconds !== undefined) {
      item.created.seconds = item.created._seconds;
      item.created.nanoseconds = item.created._nanoseconds;
    }
  }

  /**
   * Helper function to check if applicant should be included (extracted filtering logic)
   */
  private shouldIncludeApplicant(item: any): boolean {
    return true;
    if (item.origin === 'JOIN') {
      return (
        (item.firstName != undefined) &&
        item.email != null &&
        item.phoneNumber != null
      );
    } else if (item.logs && item.logs.length > 0) {
      return true;
    } else if (item.sentSMS || item.sentEmail || item.sentPhonecall) {
      return true;
    } else if (
      item.workflowArrayExecuted != undefined &&
      item.workflowArrayExecuted.length > 0
    ) {
      return true;
    }
    return false;
  }

  /**
   * Helper function to check if applicant's created date is within range
   */
  private isApplicantInDateRange(applicant: any, start: Date, end: Date): boolean {
    if (!applicant.created) {
      return false;
    }

    // Handle both Firestore timestamp formats
    let createdDate: Date;
    if (applicant.created.toDate) {
      createdDate = applicant.created.toDate();
    } else if (applicant.created.seconds) {
      createdDate = new Date(applicant.created.seconds * 1000);
    } else if (applicant.created._seconds) {
      createdDate = new Date(applicant.created._seconds * 1000);
    } else {
      return false;
    }

    return createdDate >= start && createdDate <= end;
  }

  /**
   * Fetch applicants between two dates (inclusive) by filtering from existing service data.
   * Uses returnAllApplicants() and filters in-memory instead of making database calls.
   */
  async fetchApplicantsBetween(start: Date, end: Date): Promise<any[]> {
    try {
      // First, try to use existing applicants from the service
      let allApplicants = this.returnAllApplicants();

      // If no applicants are loaded, we need to fetch them first
      // This maintains backward compatibility but prefers using cached data
      if (!allApplicants || allApplicants.length === 0) {
        // Fallback: fetch from database if service cache is empty
        const snap = await this.customerDatabaseService
          .getApplicants()
          .where('created', '>=', start)
          .where('created', '<=', end)
          .get();

        const fetchedApplicants: any[] = [];
        const docs = snap?.docs || [];

        if (docs.length > 0) {
          docs.forEach((obj: any) => {
            const item = obj.data();
            item.id = obj.id;
            this.normalizeApplicant(item);

            if (this.shouldIncludeApplicant(item)) {
              fetchedApplicants.push(item);
            }
          });
        }

        // Cache the fetched applicants
        this.allApplicants = fetchedApplicants;
        this.isApplicantDataFetchedSubject.next(true);
        this.isApplicantDataUpdateNeededSubject.next(false);

        return fetchedApplicants;
      }

      // Filter existing applicants by date range and inclusion criteria
      const filteredApplicants = allApplicants
        .filter((applicant) => {
          // First check date range
          if (!this.isApplicantInDateRange(applicant, start, end)) {
            return false;
          }
          // Then check inclusion criteria
          return this.shouldIncludeApplicant(applicant);
        })
        .map((applicant) => {
          // Ensure normalization is applied
          const normalized = { ...applicant };
          this.normalizeApplicant(normalized);
          return normalized;
        });

      return filteredApplicants;
    } catch (error) {
      console.error('Error fetching applicants between dates:', error);
      return [];
    }
  }

  /**
   * function to get the contigent applicants. We have a function here because it is noticeably slower if we get the contigent from allOldAndNewApplicants
   * as it fetch everything even before the contigent.
   */
  private async fetchContingentApplicants(timestamp: any) {
    this.contingentApplicants = [];
    await this.customerDatabaseService.getContingentApplicants(timestamp).then((array) => {
      this.contingentApplicants = array;
    });
    return this.contingentApplicants;
  }

  getContingentApplicants() {
    return this.contingentApplicants;
  }

  /**
   * @param locale the supported language code for the locale (as it is now only 4). If we add a lot, maybe consider having constants 
   * @returns the remaining contingent as a map with the locale month + locale year as key and the remaining contingent as value.
   */
  async findingApplicantsPerMonth(locale: 'en_GB' | 'de_DE' | 'fr_FR' | 'it_IT') {
    const planLimits = JSON.parse(localStorage.getItem("planLimits"));

    console.log("Plan Limits: ", planLimits);

    if (planLimits === null) {
      return null;
    }

    const startDateForContigent = planLimits.startDateForContigent ? planLimits.startDateForContigent : planLimits.startDateforContigent;
    if (planLimits == undefined || planLimits == null || planLimits.poolContigent == undefined || planLimits.poolContigent == null ||
      startDateForContigent == undefined || startDateForContigent == null) {
      return;
    }
    const poolContigent = planLimits.poolContigent;

    //getting all applicants needed.
    let contigentApplicants = this.getContingentApplicants() ? this.getContingentApplicants() : await this.fetchContingentApplicants(startDateForContigent);

    //filtering based on date and chosen parameter.
    contigentApplicants = contigentApplicants.filter((applicant) => {
      return applicant.created?.seconds >= startDateForContigent.seconds;
    });
    contigentApplicants = contigentApplicants.filter((applicant) => {
      return applicant.sentEmail || applicant.sentPhonecall || applicant.sentSMS || applicant.logs != undefined
    })

    //create key for each month after startDateForContigent
    const applicantsPerMonth = new Map();
    const startDate = moment.unix(startDateForContigent.seconds);
    const oneMonthAgo = moment().subtract(1, 'months');
    const monthCount = oneMonthAgo.diff(startDate, 'months');
    for (let i = 0; i <= monthCount; i++) {
      const key = moment.unix(startDateForContigent.seconds).locale(locale).add(i, 'months').format('MMMM YYYY');
      applicantsPerMonth.set(key, 0);
    }

    //add applicants to the respective month (skip the current month.)
    const thisMonth = moment().locale(locale).month();
    contigentApplicants.forEach((applicant) => {
      const key = moment(applicant.created.toDate()).locale(locale);
      if (key.month() !== thisMonth) {
        const monthFormat = key.format('MMMM YYYY');
        const monthValue = applicantsPerMonth.get(monthFormat);
        applicantsPerMonth.set(monthFormat, monthValue + 1);
      }
    })

    //reducing the poolContingent from each month and adding the remaining to the next month
    let finalRemainingContigent = 0;
    applicantsPerMonth.forEach((amountApplicants, month) => {
      finalRemainingContigent = finalRemainingContigent - amountApplicants + poolContigent;
      applicantsPerMonth.set(month, finalRemainingContigent < 0 ? 0 : finalRemainingContigent);
    })

    return applicantsPerMonth;
  }

  getApplicantData(id) {
    return this.allApplicants.find((applicant) => applicant.id === id);
  }
}
